/**
 * The Help Center slide-out, mounted once for the whole app. It is the shared
 * `HelpCenter` from `@ampersandbrands/ui` (articles, report a problem, my
 * requests); this file supplies the Sweet Spot half: the Service Desk client,
 * the store audience, and the page the reader is on.
 *
 * `client` is created once and shared with the header, so the unread badge and
 * the panel talk to the same instance.
 */

import { useLocation } from 'react-router-dom';
import { HelpCenter, type HelpCenterView, type SdPageContext, type ServiceDeskClient } from '@ampersandbrands/ui';
import { captureSweetSpotScreenshot } from '@/lib/supportScreenshot';
import { toolLabelForPath } from '@/lib/helpClient';
import { useStoreAssignment } from '@/lib/useStores';
import { resolveRoutePath, resolveRouteTitle } from '@/shell/routeTitles';

export function HelpAndSupport({
  open,
  onClose,
  client,
  onActivity,
  initialView,
}: {
  open: boolean;
  onClose: () => void;
  /** Open on this view (a request from a /?help_ticket= link) instead of the home menu. */
  initialView?: HelpCenterView;
  client: ServiceDeskClient;
  onActivity?: () => void;
}) {
  const location = useLocation();
  const storeAssignment = useStoreAssignment();

  const tool = toolLabelForPath(location.pathname);
  const titleParts = resolveRouteTitle(location.pathname);
  const matchedPath = resolveRoutePath(location.pathname);

  // A store account is locked to its own store, so only a corporate user's
  // header pick is worth reporting.
  const page: SdPageContext = {
    app: 'sweetspot',
    route: `${location.pathname}${location.search}`,
    url: window.location.href,
    page_title: titleParts ? titleParts[titleParts.length - 1] : document.title,
    tool_label: tool,
    page_key: matchedPath ? `sweetspot:${matchedPath}` : undefined,
    page_state:
      storeAssignment.kind === 'corporate' && storeAssignment.storeNo !== undefined
        ? [{ label: 'Selected store', value: `${storeAssignment.storeNo} ${storeAssignment.storeName}` }]
        : undefined,
    captureScreenshot: async () => {
      const shot = await captureSweetSpotScreenshot(tool);
      return shot ? { filename: shot.filename, content_type: shot.content_type, base64: shot.base64 } : null;
    },
  };

  return (
    <HelpCenter
      open={open}
      onClose={onClose}
      client={client}
      page={page}
      audience="stores"
      onActivity={onActivity}
      initialView={initialView}
    />
  );
}
